/**
 * Tenant selector component with modern styling
 * Task 32: Updated to show config status
 */

import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '../UI/Select';
import { listTenants } from '../../api/tenants';
import { useWorkspace } from '../../hooks/useWorkspace';
import type { Tenant } from '../../types/api';
import { Building2, Cloud, Server, Loader2 } from 'lucide-react';

interface TenantSelectorProps {
  defaultTenantId?: string;
  showConfigStatus?: boolean;
}

export function TenantSelector({ defaultTenantId, showConfigStatus = true }: TenantSelectorProps = {}) {
  const { 
    tenant, 
    tenants, 
    selectTenant, 
    setTenants,
    tenantConfig,
    tenantConfigLoading,
    hasCustomConfig,
    deploymentType,
    clearWorkspace,
  } = useWorkspace();
  
  const { data, isPending } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: listTenants,
  });

  // Track if we've initialized from localStorage
  const [hasInitialized, setHasInitialized] = React.useState(false);

  useEffect(() => {
    if (data) {
      setTenants(data);
    }
  }, [data, setTenants]);

  // Auto-select default tenant ONLY if no tenant is currently selected
  // Don't force-switch tenants on page navigation - respect user's existing selection
  // EXCEPTION: If tenant is selected but doesn't match defaultTenantId, re-select (fixes refresh issue)
  useEffect(() => {
    // Skip if already initialized (prevents re-running on every mount)
    if (hasInitialized) {
      return;
    }

    if (data && defaultTenantId) {
      const defaultTenant = data.find((t: Tenant) => t.id === defaultTenantId);
      
      if (!defaultTenant) {
        console.warn(`[TenantSelector] Default tenant ID ${defaultTenantId} not found in tenants list`);
        setHasInitialized(true);
        return;
      }
      
      // Auto-select if:
      // 1. No tenant is selected
      // 2. Current tenant doesn't match the default (fixes refresh/state change issues)
      if (!tenant || tenant.id !== defaultTenantId) {
        if (tenant && tenant.id !== defaultTenantId) {
          console.log('[TenantSelector] Correcting tenant to match default:', {
            current: tenant.name,
            expected: defaultTenant.name
          });
        } else {
          console.log('[TenantSelector] Auto-selecting default tenant:', defaultTenant.name);
        }
        selectTenant(defaultTenant);
        setHasInitialized(true);
      } else {
        // Tenant already selected and matches default
        console.log('[TenantSelector] Tenant already selected and matches default:', tenant.name);
        setHasInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultTenantId]); // Only depend on data and defaultTenantId

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = (data || tenants).find((t: Tenant) => t.id === e.target.value);
    if (selected) {
      selectTenant(selected);
    }
  };

  if (isPending) {
    return (
      <div className="w-64 h-10 bg-slate-100 animate-pulse rounded-lg"></div>
    );
  }

  const options: Array<{ value: string; label: string; disabled?: boolean }> = [
    { value: '', label: 'Select tenant...', disabled: true },
    ...(data || tenants).map((t: Tenant) => ({ value: t.id, label: t.name }))
  ];

  // Determine status badge
  const getStatusBadge = () => {
    if (!tenant || !showConfigStatus) return null;
    
    if (tenantConfigLoading) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
          <Loader2 className="w-3 h-3 animate-spin" />
        </span>
      );
    }

    // Show deployment type badge if config exists (even if not "custom")
    if (tenantConfig && deploymentType && deploymentType !== 'shared') {
      const getDeploymentLabel = () => {
        switch (deploymentType) {
          case 'dedicated':
            return 'Dedicated';
          case 'customer_cloud':
            return 'Custom';
          case 'hybrid':
            return 'Hybrid';
          case 'on_premise':
            return 'On-Premise';
          default:
            return 'Custom';
        }
      };

      const getDeploymentIcon = () => {
        switch (deploymentType) {
          case 'on_premise':
            return Server;
          case 'customer_cloud':
          case 'hybrid':
            return Cloud;
          default:
            return Cloud;
        }
      };

      const Icon = getDeploymentIcon();
      
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
          title={`Deployment: ${deploymentType}`}
        >
          <Icon className="w-3 h-3" />
          {getDeploymentLabel()}
        </span>
      );
    }

    // Only show "Shared" if no config or explicitly shared
    if (!tenantConfig || deploymentType === 'shared' || !hasCustomConfig) {
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600"
          title="Using shared core services"
        >
          <Server className="w-3 h-3" />
          Shared
        </span>
      );
    }

    // Fallback: show Custom if hasCustomConfig
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
        title={`Deployment: ${deploymentType || 'custom'}`}
      >
        <Cloud className="w-3 h-3" />
        Custom
      </span>
    );
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <Building2 className="h-4 w-4 text-slate-400" />
        </div>
        <select
          value={tenant?.id || ''}
          onChange={handleChange}
          className="w-64 h-10 pl-10 pr-10 text-sm font-semibold bg-white border-2 border-slate-200 rounded-lg hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none cursor-pointer text-slate-700"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {getStatusBadge()}
    </div>
  );
}
